import { get } from 'svelte/store';
import { devices } from 'src/stores/lists/devices';
import { celsius_to_fahrenheit, fahrenheit_to_celsius, shellyId16 } from 'src/helpers/interface_helpers';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import type { ITempSchemeObj } from 'src/types/TemperatureSchemeType';

export function handleThermostatTemps(deftemp, units, isinfh) {
	if (units === 'F' && !isinfh) {
		return celsius_to_fahrenheit(deftemp || 21);
	} else if (units === 'C' && isinfh) {
		return fahrenheit_to_celsius(deftemp || 72);
	} else {
		return deftemp ? deftemp : units === 'C' ? 21 : 72;
	}
}

export function convertExtTempSensorValue(extTempSensorValue, units) {
	if (typeof extTempSensorValue === 'undefined') return;
	let tempUnit;
	let tempVal;
	let convertedNumbers;

	tempUnit = extTempSensorValue.includes('C') ? 'C' : 'F';
	tempVal = extTempSensorValue.split(' ')[0];

	convertedNumbers = handleThermostatTemps(tempVal, units, tempUnit === 'F');

	return `${tempVal === 'N/A' ? tempVal : convertedNumbers} °${units}`;
}

export function findExtTempSensor(extSensorsStatuses, thermostat, deviceSensor) {
	try {
		if (typeof thermostat !== 'object') return;

		let extTempSensorId;
		let tmpId = thermostat?.tschem?.r.split(':')[2];
		let devSensorCategory = deviceSensor ? deviceSensor.category : undefined;
		// light, relay - t101, t1;
		// thermostat - tmp101
		// sensors - tmp

		if (devSensorCategory === 'sensors') {
			extTempSensorId = tmpId;
		} else if (devSensorCategory === 'thermostat') {
			extTempSensorId = Number(tmpId.substr(3)) - 1;
		} else {
			extTempSensorId =
				devSensorCategory === 'relay' || tmpId.length > 2 ? Number(tmpId.substr(1)) - 1 : tmpId;
		}

		let extTempSensor = extSensorsStatuses.find((el) => {
			if (el?.sensor && el.sensor.hasOwnProperty('extid')) {
				if (el?.sensor?.type === 'temperature' && el.sensor.extid == extTempSensorId) return el;
			} else if (isXT1Module(deviceSensor?.type)) {
				if (el.component == `number:${Number(tmpId.replace('number', '')) - 1}`) return el;
			} else {
				if (
					el?.sensor?.type == 'temperature' ||
					el.name === 'temp' ||
					el.name == 'temperature' ||
					el.name == 'temperature:0' // Weather Station
				)
					return el;
			}
		});

		return extTempSensor;
	} catch (error) {
		return undefined;
	}
}

/**
 * Finds the peripheral device ID based on a parent device ID and sensor channel.
 * @param {string} sensorDeviceID - The base (parent) device ID.
 * @param {string} sensorID - The sensor's channel.
 * @returns {string} - The peripheral device ID if it exists, otherwise the base device ID.
 */
const findPeripheralDeviceID = (sensorDeviceID: string, sensorID: string): string => {
	const peripheralDeviceID = `${sensorDeviceID}_${sensorID}`;
	const peripheralDevice = get(devices)[peripheralDeviceID];
	return peripheralDevice?.peripheral_type === 'temperature' ? peripheralDeviceID : sensorDeviceID;
};

/**
 * Resolves the full device ID of a sensor, including potential peripheral extension.
 * @param {string} sensorDecId - The decimal device ID from the scheme.
 * @param {ITempSchemeObj} thermostat - The thermostat config.
 * @returns {string | undefined} - The resolved full device ID, or undefined if invalid.
 */
export const findDeviceSensorID = (
	sensorDecId: string,
	thermostat: ITempSchemeObj,
): string | undefined => {
	const deviceID = sensorDecId?.includes('X')
		? sensorDecId // GBLE & XMOD sensors
		: isNaN(Number(sensorDecId))
			? undefined
			: shellyId16(sensorDecId);
	return findPeripheralDeviceID(deviceID, thermostat?._meta?.ext_ind);
};
